import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  ChatBubbleBottomCenterTextIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CallLogDetailsModal, { traduzirEndedReason, getStatusBadgeClass } from '../components/CallLogDetailsModal';

const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
const selectClassName =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export default function CallLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const callIdFromUrl = searchParams.get('callId');
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [filterCostMin, setFilterCostMin] = useState('');
  const [filterCostMax, setFilterCostMax] = useState('');
  const [filterAssistantId, setFilterAssistantId] = useState('');
  const [filterPhoneNumber, setFilterPhoneNumber] = useState('');
  const [filterCustomerNumber, setFilterCustomerNumber] = useState('');
  const [filterEndedReason, setFilterEndedReason] = useState('all');
  const [filterCallId, setFilterCallId] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [endedReasonOptions, setEndedReasonOptions] = useState([]);
  const [filteredCount, setFilteredCount] = useState(0);
  const [clienteTaxa, setClienteTaxa] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
  // const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const clienteId = userData.clienteId || userData._id || userData.id;
  const isAdmin = userData.role === 'admin' || userData.role === 'super_admin';
  const tokenClient = localStorage.getItem('tokenClient');

  const buildStableKey = (prefix, idValue, index) => {
    if (idValue !== undefined && idValue !== null && String(idValue).trim() !== '') {
      return `${prefix}-${String(idValue)}`;
    }
    return `${prefix}-idx-${index}`;
  };

  const isExclusaoFollowUp = (value) => {
    const txt = String(value || '').toLowerCase();
    if (!txt) return false;
    return txt.includes('numero_excluido_followup') ||
      txt.includes('número está na lista de exclusão do follow-up') ||
      txt.includes('numero na lista de exclusao do follow-up') ||
      txt.includes('lista de exclus') ||
      txt.includes('exclu');
  };

  const flattenStructuredOutput = (value, prefix = '', target = {}) => {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        target[prefix || 'valor'] = '';
        return target;
      }
      value.forEach((item, index) => {
        const key = prefix ? `${prefix}[${index}]` : `[${index}]`;
        flattenStructuredOutput(item, key, target);
      });
      return target;
    }
    if (value && typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        target[prefix || 'valor'] = '';
        return target;
      }
      entries.forEach(([key, nestedValue]) => {
        const composedKey = prefix ? `${prefix}.${key}` : key;
        flattenStructuredOutput(nestedValue, composedKey, target);
      });
      return target;
    }
    target[prefix || 'valor'] = value ?? '';
    return target;
  };

  const formatStructuredCellValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    try {
      return JSON.stringify(value);
    } catch (_err) {
      return String(value);
    }
  };

  const buildStructuredOutputColumns = (rawStructuredOutput) => {
    if (!rawStructuredOutput || typeof rawStructuredOutput !== 'object' || Array.isArray(rawStructuredOutput)) {
      return {};
    }
    const values = Object.values(rawStructuredOutput);
    const looksLikeNamedStructures =
      values.length > 0 &&
      values.every((item) => item && typeof item === 'object' && !Array.isArray(item) && item.name);

    if (looksLikeNamedStructures) {
      const columns = {};
      values.forEach((item, index) => {
        const baseName = String(item.name || '').trim() || `Estrutura ${index + 1}`;
        let columnName = baseName;
        let suffix = 2;
        while (Object.prototype.hasOwnProperty.call(columns, columnName)) {
          columnName = `${baseName} (${suffix})`;
          suffix += 1;
        }
        const preferredValue =
          item.result ??
          item.output ??
          item.value ??
          item.answer ??
          '';
        columns[columnName] = preferredValue;
      });
      return columns;
    }

    return flattenStructuredOutput(rawStructuredOutput);
  };

  const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("Sessão expirada. Faça login novamente.");
      return null;
    }
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchPhoneNumbersAndAssistants = async () => {
    try {
      const authConfig = getAuthConfig();
      if (!authConfig) {
        return;
      }

      const params = !isAdmin && clienteId ? { clienteId } : {};

      if (tokenClient) {
        params.tokenClient = tokenClient;
      }
      console.log('[CallLogs] Buscando telefones e assistentes com params:', params);
      const [phones, assistantsData] = await Promise.all([
        axios.get(`${baseUrl}/api/phoneNumbers`, { ...authConfig, params }),
        axios.get(`${baseUrl}/api/assistentes/master`, { ...authConfig, params })
      ]);
      console.log('[CallLogs] Telefones recebidos do backend:', phones.data);
      console.log('[CallLogs] Assistentes recebidos do backend:', assistantsData.data);
      setPhoneNumbers(phones.data);
      setAssistants(assistantsData.data);
    } catch (err) {
      console.error('[CallLogs] Erro ao buscar telefones e assistentes:', err);
      setError(err.message);
    }
  };

  const fetchCallLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = !isAdmin && clienteId ? { clienteId } : {};
      params.sort = '-startedAt';
      params.page = currentPage;
      params.per_page = itemsPerPage;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (filterDateStart) params.dateStart = filterDateStart;
      if (filterDateEnd) params.dateEnd = filterDateEnd;
      if (filterCostMin !== '') params.costMin = filterCostMin;
      if (filterCostMax !== '') params.costMax = filterCostMax;
      if (filterAssistantId) params.assistantId = filterAssistantId;
      if (filterPhoneNumber) params.phoneNumber = filterPhoneNumber;
      if (filterCustomerNumber.trim()) params.customerNumber = filterCustomerNumber.trim();
      if (filterEndedReason && filterEndedReason !== 'all') params.endedReason = filterEndedReason;
      if (filterCallId.trim()) params.callId = filterCallId.trim();
      console.log('[CallLogs] Buscando call logs com params:', params);
      const response = await axios.get(`${baseUrl}/api/callReports`, {
        params,
        ...getAuthConfig(),
      });
      console.log('[CallLogs] Resposta completa do backend:', response.data);
      console.log('[CallLogs] Dados das chamadas recebidos:', response.data.data);
      console.log('[CallLogs] Paginação:', response.data.pagination);
      console.log('[CallLogs] Estatísticas:', response.data.stats);
      console.log('[CallLogs] Total de chamadas:', response.data.pagination?.total);
      console.log('[CallLogs] Chamadas bem-sucedidas:', response.data.stats?.successful);
      console.log('[CallLogs] Chamadas falhadas:', response.data.stats?.failed);
      
      // Log detalhado de cada chamada
      if (response.data.data && Array.isArray(response.data.data)) {
        console.log('[CallLogs] Detalhes das chamadas:');
        response.data.data.forEach((log, index) => {
          console.log(`[CallLogs] Chamada ${index + 1}:`, {
            _id: log._id,
            callId: log.call?.id,
            customer: log.call?.customer,
            assistantId: log.assistant?.id || log.call?.assistantId,
            endedReason: log.endedReason,
            status: log.status,
            startedAt: log.startedAt,
            durationSeconds: log.durationSeconds,
            cost: log.cost,
            custoFinal: log.custoFinal,
            transcript: log.transcript ? 'Disponível' : 'Não disponível',
            recordingUrl: log.recordingUrl ? 'Disponível' : 'Não disponível'
          });
        });
      }
      
      setCallLogs(response.data.data);
      setFilteredCount(response.data.pagination.total);
      setStats({
        total: response.data.pagination.total,
        successful: response.data.stats?.successful ?? 0,
        failed: response.data.stats?.failed ?? 0,
      });
      const distinctReasons = [];
      (response.data.distinctEndedReasons || []).forEach(reason => {
        if (!reason) return;
        const trimmed = String(reason).trim();
        if (!trimmed) return;
        const normalized = trimmed.toLowerCase();
        if (!distinctReasons.some(entry => entry.toLowerCase() === normalized)) {
          distinctReasons.push(trimmed);
        }
      });
      setEndedReasonOptions(distinctReasons.sort((a, b) => a.localeCompare(b)));
    } catch (err) {
      setError('Falha ao carregar os registros de chamadas.');
      console.error('[CallLogs] Erro ao buscar call logs:', err);
      if (err.response) {
        console.error('[CallLogs] Resposta de erro do servidor:', err.response.data);
        console.error('[CallLogs] Status do erro:', err.response.status);
      }
    } finally {
      setLoading(false);
    }
  }, [
    clienteId,
    isAdmin,
    baseUrl,
    currentPage,
    filterStatus,
    filterDateStart,
    filterDateEnd,
    filterCostMin,
    filterCostMax,
    filterAssistantId,
    filterPhoneNumber,
    filterCustomerNumber,
    filterEndedReason,
    filterCallId,
  ]);

  // Adicione o estado para stats
  const [stats, setStats] = useState({
    total: 0,
    successful: 0,
    failed: 0,
  });
  const [messageLogs, setMessageLogs] = useState([]);
  const [messageLogsLoading, setMessageLogsLoading] = useState(false);
  const [followUpCallLogs, setFollowUpCallLogs] = useState([]);
  const [followUpCallLogsLoading, setFollowUpCallLogsLoading] = useState(false);
  const [selectedFollowUpCall, setSelectedFollowUpCall] = useState(null);
  const [filterMessageScope, setFilterMessageScope] = useState('all');
  const [filterMessageStatus, setFilterMessageStatus] = useState('all');
  const [filterFollowUpCallStatus, setFilterFollowUpCallStatus] = useState('all');
  const [filterMessageClienteId, setFilterMessageClienteId] = useState('all');
  const [clientesMessageFilter, setClientesMessageFilter] = useState([]);
  const [logsViewMode, setLogsViewMode] = useState('calls'); // calls | messages | followup_calls

  useEffect(() => {
    fetchCallLogs();
    fetchPhoneNumbersAndAssistants(); // Adicione esta linha

  }, [fetchCallLogs]);

  const fetchGlobalMessageLogs = useCallback(async () => {
    setMessageLogsLoading(true);
    try {
      const authConfig = getAuthConfig();
      if (!authConfig) {
        setMessageLogsLoading(false);
        return;
      }
      const params = {
        page: 1,
        per_page: 50,
      };
      if (filterDateStart) params.dateStart = filterDateStart;
      if (filterDateEnd) params.dateEnd = filterDateEnd;
      if (filterCustomerNumber.trim()) params.customerNumber = filterCustomerNumber.trim();
      if (filterMessageScope !== 'all') params.escopo = filterMessageScope;
      if (filterMessageStatus !== 'all') params.status = filterMessageStatus;
      if (isAdmin && filterMessageClienteId !== 'all') params.clienteId = filterMessageClienteId;
      const res = await axios.get(`${baseUrl}/api/callReports/followup-message-logs`, {
        params,
        ...authConfig,
      });
      setMessageLogs(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('[CallLogs] Erro ao buscar histórico global de mensagens:', err);
      setMessageLogs([]);
    } finally {
      setMessageLogsLoading(false);
    }
  }, [baseUrl, filterDateStart, filterDateEnd, filterCustomerNumber, filterMessageScope, filterMessageStatus, filterMessageClienteId, isAdmin]);

  const getTranscriptPreview = (text, max = 140) => {
    const normalized = String(text || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return '-';
    if (normalized.length <= max) return normalized;
    return `${normalized.slice(0, max)}...`;
  };

  useEffect(() => {
    fetchGlobalMessageLogs();
  }, [fetchGlobalMessageLogs]);

  const fetchFollowUpCallLogs = useCallback(async () => {
    setFollowUpCallLogsLoading(true);
    try {
      const authConfig = getAuthConfig();
      if (!authConfig) {
        setFollowUpCallLogsLoading(false);
        return;
      }
      const params = {
        page: 1,
        per_page: 100,
      };
      if (filterDateStart) params.dateStart = filterDateStart;
      if (filterDateEnd) params.dateEnd = filterDateEnd;
      if (filterCustomerNumber.trim()) params.customerNumber = filterCustomerNumber.trim();
      if (filterMessageScope !== 'all') params.escopo = filterMessageScope;
      if (filterFollowUpCallStatus !== 'all') params.status = filterFollowUpCallStatus;
      if (isAdmin && filterMessageClienteId !== 'all') params.clienteId = filterMessageClienteId;
      const res = await axios.get(`${baseUrl}/api/callReports/followup-call-logs`, {
        params,
        ...authConfig,
      });
      setFollowUpCallLogs(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('[CallLogs] Erro ao buscar histórico de chamadas de follow-up:', err);
      setFollowUpCallLogs([]);
    } finally {
      setFollowUpCallLogsLoading(false);
    }
  }, [baseUrl, filterDateStart, filterDateEnd, filterCustomerNumber, filterMessageScope, filterFollowUpCallStatus, filterMessageClienteId, isAdmin]);

  useEffect(() => {
    fetchFollowUpCallLogs();
  }, [fetchFollowUpCallLogs]);

  const handleExportFollowUpCallsXlsx = async () => {
    try {
      const authConfig = getAuthConfig();
      if (!authConfig) return;
      const params = {};
      params.page = 1;
      params.per_page = 10000;
      if (filterDateStart) params.dateStart = filterDateStart;
      if (filterDateEnd) params.dateEnd = filterDateEnd;
      if (filterCustomerNumber.trim()) params.customerNumber = filterCustomerNumber.trim();
      if (filterMessageScope !== 'all') params.escopo = filterMessageScope;
      if (filterFollowUpCallStatus !== 'all') params.status = filterFollowUpCallStatus;
      if (isAdmin && filterMessageClienteId !== 'all') params.clienteId = filterMessageClienteId;
      const response = await axios.get(`${baseUrl}/api/callReports/followup-call-logs`, {
        params,
        ...authConfig,
      });
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];

      const wb = XLSX.utils.book_new();
      const resumoData = [
        { Campo: 'Período (início)', Valor: filterDateStart || '-' },
        { Campo: 'Período (fim)', Valor: filterDateEnd || '-' },
        { Campo: 'Escopo', Valor: filterMessageScope === 'all' ? 'Todos' : filterMessageScope },
        { Campo: 'Status', Valor: filterMessageStatus === 'all' ? 'Todos' : filterMessageStatus },
        { Campo: 'Total de chamadas Follow-up', Valor: String(rows.length) },
      ];
      const wsResumo = XLSX.utils.json_to_sheet(resumoData);
      XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo Relatorio');

      const rowsWithStructured = rows.map((r) => ({
        ...r,
        structuredFlat: buildStructuredOutputColumns(r.structuredOutputs || {}),
      }));
      const structuredColumns = Array.from(new Set(
        rowsWithStructured.flatMap((item) => Object.keys(item.structuredFlat || {}))
      )).filter((key) => String(key || '').trim().toLowerCase() !== 'valor');

      const detailRows = rowsWithStructured.map((r) => {
        const row = {
          'Data/Hora': formatDateTime(r.createdAt),
          ...(isAdmin ? { 'Cliente': r.clienteNome || '-' } : {}),
          'Escopo': r.escopo === 'campanha' ? 'Campanha' : 'Global',
          'Campanha': r.campanhaNome || '-',
          'Numero Cliente': r.customerNumber || '-',
          'Assistente': r.assistantName || r.assistantId || '-',
          'Telefone Follow-up': (r.phoneNumberName || r.phoneNumberNumber)
            ? `${r.phoneNumberName || 'Telefone'}${r.phoneNumberNumber ? ` (${r.phoneNumberNumber})` : ''}`
            : (r.phoneNumberId || '-'),
          'Status': r.statusExibicao || (r.status === 'started' ? 'Iniciada' : 'Falhou'),
          'Call ID': r.callId || '-',
          'Detalhe': r.reason || '-',
          'Transcrição': r.transcricao || '-',
        };

        structuredColumns.forEach((columnKey) => {
          row[columnKey] = formatStructuredCellValue(r.structuredFlat?.[columnKey]);
        });

        return row;
      });
      const wsCalls = XLSX.utils.json_to_sheet(detailRows);
      wsCalls['!cols'] = [
        { wch: 18 },
        ...(isAdmin ? [{ wch: 24 }] : []),
        { wch: 10 },
        { wch: 24 },
        { wch: 18 },
        { wch: 26 },
        { wch: 26 },
        { wch: 10 },
        { wch: 38 },
        { wch: 40 },
        { wch: 80 },
        ...structuredColumns.map((columnKey) => ({ wch: Math.max(24, Math.min(50, columnKey.length + 16)) })),
      ];
      XLSX.utils.book_append_sheet(wb, wsCalls, 'Chamadas Follow-up');
      XLSX.writeFile(wb, 'relatorio-chamadas-followup.xlsx');
    } catch (err) {
      console.error('[CallLogs] Erro ao exportar chamadas de follow-up:', err);
      toast.error('Erro ao exportar relatório de Follow-up (Excel).');
    }
  };

  useEffect(() => {
    const fetchClientesForMessageFilter = async () => {
      if (!isAdmin) return;
      try {
        const authConfig = getAuthConfig();
        if (!authConfig) return;
        const res = await axios.get(`${baseUrl}/api/usuarios`, authConfig);
        setClientesMessageFilter(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('[CallLogs] Erro ao buscar clientes para filtro de mensagens:', err);
      }
    };
    fetchClientesForMessageFilter();
  }, [isAdmin, baseUrl]);

  // Efeito para abrir o modal automaticamente quando há callId na URL
  useEffect(() => {
    if (callIdFromUrl && !selectedCall) {
      // Primeiro, tentar encontrar na lista já carregada
      const call = callLogs.find(log => {
        const logCallId = log.call?.id || log._id || '';
        return logCallId === callIdFromUrl || 
               String(logCallId).includes(callIdFromUrl) ||
               String(callIdFromUrl).includes(logCallId);
      });
      
      if (call) {
        const assistantId = call.assistant?.id || call.call?.assistantId || call.assistantId || call.call?.assistant?.id;
        const assistant = findAssistantById(assistantId);
        const assistantName =
          call.assistantName ||
          assistant?.name ||
          assistant?.nome ||
          call.assistant?.name ||
          'Desconhecido';
        setSelectedCall({ ...call, assistantName });
        // Remover o callId da URL após abrir o modal
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('callId');
        setSearchParams(newSearchParams, { replace: true });
        return;
      }

      // Se não encontrou na lista carregada, buscar diretamente pela API
      const fetchCallById = async () => {
        try {
          const params = !isAdmin && clienteId ? { clienteId } : {};
          // Buscar todas as páginas para encontrar a chamada
          const response = await axios.get(`${baseUrl}/api/callReports`, {
            params: { ...params, page: 1, per_page: 100 },
            ...getAuthConfig(),
          });
          
          if (response.data.data && Array.isArray(response.data.data)) {
            const foundCall = response.data.data.find(log => {
              const logCallId = log.call?.id || log._id || '';
              return logCallId === callIdFromUrl || 
                     String(logCallId).includes(callIdFromUrl) ||
                     String(callIdFromUrl).includes(logCallId);
            });
            
            if (foundCall) {
              // Adicionar assistantName ao call encontrado
              const assistantId = foundCall.assistant?.id || foundCall.call?.assistantId || foundCall.assistantId || foundCall.call?.assistant?.id;
              const assistant = findAssistantById(assistantId);
              const callWithAssistant = {
                ...foundCall,
                assistantName: assistant?.name || assistant?.nome || foundCall.assistant?.name || 'Desconhecido'
              };
              setSelectedCall(callWithAssistant);
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.delete('callId');
              setSearchParams(newSearchParams, { replace: true });
            } else {
              // Tentar buscar pelo ID do documento diretamente
              try {
                const directResponse = await axios.get(`${baseUrl}/api/callReports/${callIdFromUrl}`, getAuthConfig());
                if (directResponse.data) {
                  // Adicionar assistantName ao call encontrado
                  const assistantId = directResponse.data.assistant?.id || directResponse.data.call?.assistantId || directResponse.data.assistantId || directResponse.data.call?.assistant?.id;
                  const assistant = findAssistantById(assistantId);
                  const callWithAssistant = {
                    ...directResponse.data,
                    assistantName: assistant?.name || assistant?.nome || directResponse.data.assistant?.name || 'Desconhecido'
                  };
                  setSelectedCall(callWithAssistant);
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.delete('callId');
                  setSearchParams(newSearchParams, { replace: true });
                }
              } catch (directErr) {
                console.error('Chamada não encontrada:', directErr);
                toast.error('Chamada não encontrada');
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('callId');
                setSearchParams(newSearchParams, { replace: true });
              }
            }
          }
        } catch (err) {
          console.error('Erro ao buscar chamada por ID:', err);
          toast.error('Erro ao buscar chamada');
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('callId');
          setSearchParams(newSearchParams, { replace: true });
        }
      };
      
      // Aguardar um pouco para garantir que os callLogs foram carregados
      const timeoutId = setTimeout(() => {
        fetchCallById();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [callIdFromUrl, callLogs, selectedCall, searchParams, setSearchParams, isAdmin, clienteId, baseUrl, assistants]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterStatus,
    filterDateStart,
    filterDateEnd,
    filterCostMin,
    filterCostMax,
    filterAssistantId,
    filterPhoneNumber,
    filterCustomerNumber,
    filterEndedReason,
    filterCallId
  ]);

  useEffect(() => {
    let isMounted = true;
    const fetchClienteTaxa = async () => {
      if (!clienteId) {
        console.log('clienteId não definido');
        return;
      }
      try {
        console.log(`Buscando taxa para clienteId: ${clienteId}`);
        const response = await axios.get(`${baseUrl}/api/usuarios/${clienteId}/taxa`, getAuthConfig());
        console.log('Resposta da API:', response.data);
        console.log('Taxa do cliente:', response.data.taxa);
        if (isMounted) setClienteTaxa(response.data.taxa || 0);
      } catch (err) {
        console.error('Erro ao buscar taxa do cliente:', err);
        if (err.response) {
          console.error('Resposta do servidor:', err.response.data);
          console.error('Status code:', err.response.status);
        } else if (err.request) {
          console.error('Requisição feita mas não houve resposta:', err.request);
        } else {
          console.error('Erro:', err.message);
        }
        if (isMounted) setClienteTaxa(0);
      }
    };
    if (clienteId) {
      fetchClienteTaxa();
    }
    return () => {
      isMounted = false;
    };
  }, [baseUrl, clienteId]);

  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined || isNaN(seconds)) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Data inválida';
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (e) {
      console.error('Erro ao formatar data:', e);
      return 'N/A';
    }
  };

  const formatMessageLogDetail = (messageLog) => {
    const statusCode = messageLog?.providerStatusCode;
    const providerResponse = messageLog?.providerResponse;
    const providerMessage =
      (providerResponse && typeof providerResponse === 'object' && (
        providerResponse.message ||
        providerResponse.error ||
        providerResponse.details
      )) || null;

    if (providerMessage && statusCode) return `${providerMessage} (status ${statusCode})`;
    if (providerMessage) return String(providerMessage);
    if (statusCode) return `Status do provedor: ${statusCode}`;
    return messageLog?.reason || '-';
  };

  const findPhoneNumberById = (id) => phoneNumbers.find(p => p.vapiId === id) || {};
  const findAssistantById = (id) => assistants.find(a => a.vapiId === id);
  const toRangeDate = (value, isEnd) => {
    if (!value) return null;
    const time = isEnd ? '23:59:59.999' : '00:00:00.000';
    const date = new Date(`${value}T${time}`);
    return isNaN(date.getTime()) ? null : date;
  };
  const matchesDateRange = (value, start, end) => {
    const startDate = toRangeDate(start, false);
    const endDate = toRangeDate(end, true);
    if (!startDate && !endDate) return true;
    const date = value ? new Date(value) : null;
    if (!date || isNaN(date.getTime())) return false;
    if (startDate && date < startDate) return false;
    if (endDate && date > endDate) return false;
    return true;
  };
  const getCostValue = (log) => {
    if (typeof log.custoFinal === 'number' && log.custoFinal > 0) return log.custoFinal;
    if (typeof log.cost === 'number') return log.cost;
    return null;
  };

  const getValidDate = (dateString) => {
    if (!dateString) return new Date(0);
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? new Date(0) : date;
  };

  // Dados já vêm filtrados do backend; apenas enriquecemos com nomes/telefones para exibição
  const displayLogs = useMemo(() => {
    return callLogs
      .map(log => {
        const phone = findPhoneNumberById(log.call?.phoneNumberId || log.phoneNumberId || log.call?.phoneNumber?.id);
        const assistantId = log.assistant?.id || log.call?.assistantId || log.assistantId || log.call?.assistant?.id;
        const assistant = findAssistantById(assistantId);
        const startedAt = log.startedAt || log.call?.startedAt || log.call?.createdAt || log.timestamp;
        const validStartedAt = getValidDate(startedAt);
        const endedReason = log.endedReason || log.call?.endedReason;
        let durationSeconds = log.durationSeconds;
        if (!durationSeconds && log.durationMs) {
          durationSeconds = Math.floor(log.durationMs / 1000);
        } else if (!durationSeconds && log.durationMinutes) {
          durationSeconds = Math.floor(log.durationMinutes * 60);
        }
        const cost = log.cost || log.call?.cost || 0;
        const custoFinal = log.custoFinal !== undefined && log.custoFinal !== null ? log.custoFinal : cost;
        const transcript = log.transcript || log.artifact?.transcript || '';
        const recordingUrl = log.recordingUrl || log.artifact?.recordingUrl || log.artifact?.recording?.url;
        const resumo = log.resumo || log.summary || log.analysis?.summary || '';
        return {
          ...log,
          assistantId: assistantId || '',
          assistantName: assistant?.name || assistant?.nome || 'Desconhecido',
          assistantPhone: phone?.number || log.phoneNumber?.number || 'N/A',
          startedAt: validStartedAt,
          formattedDate: formatDateTime(startedAt),
          endedReason: endedReason,
          durationSeconds: durationSeconds,
          cost: cost,
          custoFinal: custoFinal,
          transcript: transcript,
          recordingUrl: recordingUrl,
          resumo: resumo
        };
      })
      .filter(Boolean);
  }, [callLogs, assistants, phoneNumbers]);

  const assistantOptions = useMemo(() => {
    const map = new Map();
    assistants.forEach(a => {
      const id = a.vapiId || a.id || a._id;
      if (!id) return;
      map.set(String(id), a.nome || a.name || String(id));
    });
    return Array.from(map.entries());
  }, [assistants]);
  const phoneOptions = useMemo(() => {
    const map = new Map();
    phoneNumbers.forEach(p => {
      if (!p.number) return;
      map.set(String(p.number), p.name ? `${p.name} (${p.number})` : String(p.number));
    });
    return Array.from(map.entries());
  }, [phoneNumbers]);
  const totalPages = Math.ceil(filteredCount / itemsPerPage);

  const handleExportCsv = async () => {
    try {
      const authConfig = getAuthConfig();
      if (!authConfig) return;

      const params = !isAdmin && clienteId ? { clienteId } : {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (filterDateStart) params.dateStart = filterDateStart;
      if (filterDateEnd) params.dateEnd = filterDateEnd;
      if (filterCostMin !== '') params.costMin = filterCostMin;
      if (filterCostMax !== '') params.costMax = filterCostMax;
      if (filterAssistantId) params.assistantId = filterAssistantId;
      if (filterPhoneNumber) params.phoneNumber = filterPhoneNumber;
      if (filterCustomerNumber.trim()) params.customerNumber = filterCustomerNumber.trim();
      if (filterEndedReason && filterEndedReason !== 'all') params.endedReason = filterEndedReason;
      if (filterCallId.trim()) params.callId = filterCallId.trim();

      const response = await axios.get(`${baseUrl}/api/callReports/export`, {
        params,
        ...authConfig,
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio-chamadas.csv');
      document.body.appendChild(link);
      link.click();
      // Evita NotFoundError caso o node já não esteja mais no DOM.
      try {
        link.remove(); // no-op se já estiver fora do DOM
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[CallLogs] falha ao remover link', err);
      }
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[CallLogs] Erro ao exportar CSV:', err);
      toast.error('Erro ao exportar relatório de chamadas.');
    }
  };


  return (
    <>
      <CallLogDetailsModal
        key={
          selectedCall
            ? `${selectedCall.call?.id ?? ''}|${String(selectedCall._id ?? '')}`
            : 'closed'
        }
        call={selectedCall}
        onClose={() => setSelectedCall(null)}
      />
      <CallLogDetailsModal
        key={
          selectedFollowUpCall
            ? `followup-${selectedFollowUpCall.call?.id ?? ''}|${String(selectedFollowUpCall._id ?? '')}`
            : 'followup-closed'
        }
        call={selectedFollowUpCall}
        onClose={() => setSelectedFollowUpCall(null)}
      />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Registros de Chamadas</h1>
          <p className="text-sm text-muted-foreground">Acompanhe detalhes e resultados das chamadas.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={logsViewMode === 'calls' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => setLogsViewMode('calls')}
          >
            Chamadas
          </Button>
          <Button
            variant={logsViewMode === 'messages' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => setLogsViewMode('messages')}
          >
            Mensagens de limite
          </Button>
          <Button
            variant={logsViewMode === 'followup_calls' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => setLogsViewMode('followup_calls')}
          >
            Chamadas Follow-up
          </Button>
        </div>
        {logsViewMode === 'calls' && (
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={filterStatus === 'all' ? "default" : "secondary"}
              size="sm"
              onClick={() => setFilterStatus('all')}
            >
              <span>Todas</span>
              <Badge variant="secondary" className="ml-2">
                {stats.total}
              </Badge>
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExportCsv}
            >
              Baixar relatório (CSV)
            </Button>
          </div>
        </div>
        )}
        {logsViewMode === 'calls' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Filtros</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label className="text-xs text-muted-foreground">Data - de</Label>
              <Input
                type="date"
                value={filterDateStart}
                onChange={e => setFilterDateStart(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Data - até</Label>
              <Input
                type="date"
                value={filterDateEnd}
                onChange={e => setFilterDateEnd(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Custo mínimo</Label>
              <Input
                type="number"
                step="0.01"
                value={filterCostMin}
                onChange={e => setFilterCostMin(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Custo máximo</Label>
              <Input
                type="number"
                step="0.01"
                value={filterCostMax}
                onChange={e => setFilterCostMax(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Assistente</Label>
              <select
                value={filterAssistantId}
                onChange={e => setFilterAssistantId(e.target.value)}
                className={`${selectClassName} mt-1`}
              >
                <option value="">Todos</option>
                {assistantOptions.map(([id, name], index) => (
                  <option key={buildStableKey('assistant-opt', id, index)} value={id}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Telefone (assistente)</Label>
              <select
                value={filterPhoneNumber}
                onChange={e => setFilterPhoneNumber(e.target.value)}
                className={`${selectClassName} mt-1`}
              >
                <option value="">Todos</option>
                {phoneOptions.map(([number, label], index) => (
                  <option key={buildStableKey('phone-opt', number, index)} value={number}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Número ligado</Label>
              <Input
                type="text"
                value={filterCustomerNumber}
                onChange={e => setFilterCustomerNumber(e.target.value)}
                placeholder="Ex: +5511999999999"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">ID da chamada</Label>
              <Input
                type="text"
                value={filterCallId}
                onChange={e => setFilterCallId(e.target.value)}
                placeholder="UUID ou ObjectId"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Status final</Label>
              <select
                value={filterEndedReason}
                onChange={e => setFilterEndedReason(e.target.value)}
                className={`${selectClassName} mt-1`}
              >
                <option value="all">Todos</option>
                {endedReasonOptions.map((reason, index) => (
                  <option key={buildStableKey('ended-reason', reason, index)} value={reason}>
                    {traduzirEndedReason(reason)}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>
        )}
        {logsViewMode === 'calls' && (
        <Card className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="p-4 font-semibold">Assistente</th>
                <th className="p-4 font-semibold">Número Assistente</th>
                <th className="p-4 font-semibold">Cliente</th>
                <th className="p-4 font-semibold">Status Final</th>
                <th className="p-4 font-semibold flex items-center gap-1">
                  <span>Início</span>
                  <CalendarIcon className="h-4 w-4 text-primary" />
                </th>
                <th className="p-4 font-semibold text-center">Duração</th>
                <th className="p-4 font-semibold text-center">Custo</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center p-8 text-muted-foreground">Carregando registros...</td></tr>
              ) : error ? (
                <tr><td colSpan="7" className="text-center p-8 text-destructive"><ExclamationTriangleIcon className="h-6 w-6 inline mr-2" />{error}</td></tr>
              ) : displayLogs.length === 0 ? (
                <tr><td colSpan="7" className="text-center p-8 text-muted-foreground">Nenhum registro encontrado.</td></tr>
              ) : (
                displayLogs.map((log, index) => {
                  const rowKey = buildStableKey(
                    'call-log',
                    log._id || log.call?.id || log.callId,
                    index
                  );
                  const destaqueExclusao = isExclusaoFollowUp(log?.endedReason) || isExclusaoFollowUp(log?.analysis?.summary) || isExclusaoFollowUp(log?.analysis?.finalAnalysis);
                  return (
                    <tr key={rowKey} onClick={() => setSelectedCall(log)} className={`cursor-pointer border-b border-border transition-colors hover:bg-muted/40 ${destaqueExclusao ? 'bg-amber-500/5' : ''}`}>
                      <td className="p-4 font-medium flex items-center gap-2"><ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-primary" />{log.assistantName}</td>
                      <td className="p-4 font-mono text-sm">{log.assistantPhone}</td>
                      <td className="p-4 font-mono text-sm">{log.call?.customer?.number || log.call?.customer?.name || log.customer?.number || 'N/A'}</td>
                      <td className="p-4">
                        <Badge className={destaqueExclusao ? 'bg-amber-600 text-white' : getStatusBadgeClass(log.endedReason)}>
                          {traduzirEndedReason(log.endedReason)}
                        </Badge>
                        {destaqueExclusao && (
                          <div className="mt-1 text-xs text-amber-300">Ignorado por lista de exclusão</div>
                        )}
                      </td>
                      <td className="p-4">{log.formattedDate || 'N/A'}</td>
                      <td className="p-4 text-center font-mono text-sm"><ClockIcon className="h-4 w-4 inline mr-1" />{formatDuration(log.durationSeconds)}</td>
                      <td className="p-4 text-center font-mono text-sm">
                        {(() => {
                          const costValue = log.custoFinal && log.custoFinal > 0 ? log.custoFinal : (log.cost || 0);
                          return typeof costValue === 'number' && costValue > 0 ? `$${costValue.toFixed(2)}` : 'N/A';
                        })()}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex flex-col gap-3 border-t border-border bg-muted/30 p-4 md:flex-row md:items-center md:justify-between">
              <span className="text-sm text-muted-foreground">
                Página {currentPage} de {totalPages} (Mostrando {displayLogs.length} de {filteredCount} registros)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </Card>
        )}
        {logsViewMode === 'messages' && (
        <Card className="overflow-x-auto">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Histórico global de mensagens de limite</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label className="text-xs text-muted-foreground">Escopo</Label>
                <select
                  value={filterMessageScope}
                  onChange={(e) => setFilterMessageScope(e.target.value)}
                  className={`${selectClassName} mt-1`}
                >
                  <option value="all">Todos</option>
                  <option value="campanha">Campanha</option>
                  <option value="global">Global</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Status</Label>
                <select
                  value={filterMessageStatus}
                  onChange={(e) => setFilterMessageStatus(e.target.value)}
                  className={`${selectClassName} mt-1`}
                >
                  <option value="all">Todos</option>
                  <option value="sent">Enviado</option>
                  <option value="failed">Falhou</option>
                  <option value="skipped">Ignorado</option>
                </select>
              </div>
              {isAdmin && (
                <div>
                  <Label className="text-xs text-muted-foreground">Cliente</Label>
                  <select
                    value={filterMessageClienteId}
                    onChange={(e) => setFilterMessageClienteId(e.target.value)}
                    className={`${selectClassName} mt-1`}
                  >
                    <option value="all">Todos os clientes</option>
                    {clientesMessageFilter.map((c, index) => (
                      <option key={buildStableKey('msg-cliente', c._id, index)} value={c._id}>
                        {c.nome || c.email || c._id}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {messageLogsLoading ? (
              <p className="text-center p-6 text-muted-foreground">Carregando histórico de mensagens...</p>
            ) : messageLogs.length === 0 ? (
              <p className="text-center p-6 text-muted-foreground">Nenhum envio de mensagem registrado.</p>
            ) : (
              <table className="w-full text-left text-xs">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="p-3 font-semibold">Data/Hora</th>
                    {isAdmin && <th className="p-3 font-semibold">Cliente</th>}
                    <th className="p-3 font-semibold">Número</th>
                    <th className="p-3 font-semibold">Assistente</th>
                    <th className="p-3 font-semibold">Telefone Follow-up</th>
                    <th className="p-3 font-semibold">Escopo</th>
                    <th className="p-3 font-semibold">Status</th>
                    <th className="p-3 font-semibold">Detalhe</th>
                    <th className="p-3 font-semibold">Mensagem</th>
                  </tr>
                </thead>
                <tbody>
                  {messageLogs.map((m, index) => (
                    <tr key={buildStableKey('limit-msg', m._id || m.id, index)} className="border-b border-border">
                      <td className="p-3">{formatDateTime(m.createdAt)}</td>
                      {isAdmin && <td className="p-3">{m.clienteNome || '-'}</td>}
                      <td className="p-3 font-mono">{m.customerNumber || '-'}</td>
                      <td className="p-3">{m.assistantFollowUpName || m.assistantFollowUpId || '-'}</td>
                      <td className="p-3">
                        {m.phoneNumberFollowUpName || m.phoneNumberFollowUpNumber
                          ? `${m.phoneNumberFollowUpName || 'Telefone'}${m.phoneNumberFollowUpNumber ? ` (${m.phoneNumberFollowUpNumber})` : ''}`
                          : (m.phoneNumberFollowUpId || '-')}
                      </td>
                      <td className="p-3">{m.escopo === 'campanha' ? 'Campanha' : 'Global'}</td>
                      <td className="p-3">
                        <Badge className={
                          m.status === 'sent'
                            ? 'bg-green-600 text-white'
                            : m.status === 'failed'
                              ? 'bg-red-600 text-white'
                              : 'bg-yellow-600 text-white'
                        }>
                          {m.status === 'sent' ? 'Enviado' : m.status === 'failed' ? 'Falhou' : 'Ignorado'}
                        </Badge>
                      </td>
                      <td className="p-3">{formatMessageLogDetail(m)}</td>
                      <td className="p-3 whitespace-pre-wrap break-words max-w-md">{m.messageText || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
        )}
        {logsViewMode === 'followup_calls' && (
        <Card className="overflow-x-auto">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Relatório de chamadas do Follow-up</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label className="text-xs text-muted-foreground">Data - de</Label>
                <Input
                  type="date"
                  value={filterDateStart}
                  onChange={e => setFilterDateStart(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Data - até</Label>
                <Input
                  type="date"
                  value={filterDateEnd}
                  onChange={e => setFilterDateEnd(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Número ligado</Label>
                <Input
                  type="text"
                  value={filterCustomerNumber}
                  onChange={e => setFilterCustomerNumber(e.target.value)}
                  placeholder="Ex: +5511999999999"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Escopo</Label>
                <select
                  value={filterMessageScope}
                  onChange={(e) => setFilterMessageScope(e.target.value)}
                  className={`${selectClassName} mt-1`}
                >
                  <option value="all">Todos</option>
                  <option value="campanha">Campanha</option>
                  <option value="global">Global</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Status</Label>
                <select
                  value={filterFollowUpCallStatus}
                  onChange={(e) => setFilterFollowUpCallStatus(e.target.value)}
                  className={`${selectClassName} mt-1`}
                >
                  <option value="all">Todos</option>
                  <option value="started">Iniciada</option>
                  <option value="failed">Falhou</option>
                </select>
              </div>
              {isAdmin && (
                <div>
                  <Label className="text-xs text-muted-foreground">Cliente</Label>
                  <select
                    value={filterMessageClienteId}
                    onChange={(e) => setFilterMessageClienteId(e.target.value)}
                    className={`${selectClassName} mt-1`}
                  >
                    <option value="all">Todos os clientes</option>
                    {clientesMessageFilter.map((c, index) => (
                      <option key={buildStableKey('fu-call-cliente', c._id, index)} value={c._id}>
                        {c.nome || c.email || c._id}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={handleExportFollowUpCallsXlsx}>
                Baixar relatório (Excel)
              </Button>
            </div>
            {followUpCallLogsLoading ? (
              <p className="text-center p-6 text-muted-foreground">Carregando chamadas de follow-up...</p>
            ) : followUpCallLogs.length === 0 ? (
              <p className="text-center p-6 text-muted-foreground">Nenhuma chamada de Follow-up registrada.</p>
            ) : (
              <table className="w-full text-left text-xs">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="p-3 font-semibold">Data/Hora</th>
                    {isAdmin && <th className="p-3 font-semibold">Cliente</th>}
                    <th className="p-3 font-semibold">Escopo</th>
                    <th className="p-3 font-semibold">Campanha</th>
                    <th className="p-3 font-semibold">Número</th>
                    <th className="p-3 font-semibold">Assistente</th>
                    <th className="p-3 font-semibold">Telefone Follow-up</th>
                    <th className="p-3 font-semibold">Status</th>
                    <th className="p-3 font-semibold">Observação</th>
                    <th className="p-3 font-semibold">Call ID</th>
                    <th className="p-3 font-semibold">Transcrição</th>
                  </tr>
                </thead>
                <tbody>
                  {followUpCallLogs.map((r, index) => (
                    <tr
                      key={buildStableKey('followup-call', r._id || r.id, index)}
                      className="cursor-pointer border-b border-border transition-colors hover:bg-muted/40"
                      onClick={() => setSelectedFollowUpCall({
                        _id: r._id || r.id,
                        call: {
                          id: r.callId || '',
                          customer: { number: r.customerNumber || '' },
                        },
                        assistantName: r.assistantName || r.assistantId || '',
                        endedReason: r.callStatus || r.statusExibicao || r.status || '',
                        transcript: r.transcricao || '',
                        recordingUrl: r.recordingUrl || '',
                        structuredOutputs: r.structuredOutputs || {},
                      })}
                    >
                      <td className="p-3">{formatDateTime(r.createdAt)}</td>
                      {isAdmin && <td className="p-3">{r.clienteNome || '-'}</td>}
                      <td className="p-3">{r.escopo === 'campanha' ? 'Campanha' : 'Global'}</td>
                      <td className="p-3">{r.campanhaNome || '-'}</td>
                      <td className="p-3 font-mono">{r.customerNumber || '-'}</td>
                      <td className="p-3">{r.assistantName || r.assistantId || '-'}</td>
                      <td className="p-3">
                        {r.phoneNumberName || r.phoneNumberNumber
                          ? `${r.phoneNumberName || 'Telefone'}${r.phoneNumberNumber ? ` (${r.phoneNumberNumber})` : ''}`
                          : (r.phoneNumberId || '-')}
                      </td>
                      <td className="p-3">
                        <Badge className={r.status === 'started' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}>
                          {r.statusExibicao || (r.status === 'started' ? 'Iniciada' : 'Falhou')}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs">
                        {isExclusaoFollowUp(r.reason) || isExclusaoFollowUp(r.callStatus) || isExclusaoFollowUp(r.callStatusLabel)
                          ? <span className="rounded bg-amber-600/20 px-2 py-1 text-amber-300">Ignorado por lista de exclusão</span>
                          : '-'}
                      </td>
                      <td className="p-3 font-mono">{r.callId || '-'}</td>
                      <td className="p-3 max-w-md text-xs text-muted-foreground">
                        {getTranscriptPreview(r.transcricao)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
        )}
      </div>
    </>
  );
}
